export const LATTED_TAGLINE = 'The AI content engine for organic and paid growth.';

export const NAV_LINKS = [
  { id: 'product', label: 'Product', href: '#product' },
  { id: 'use-cases', label: 'Use cases', href: '#use-cases' },
  { id: 'examples', label: 'Examples', href: '#examples' },
  { id: 'resources', label: 'Resources', href: '/blog' },
  { id: 'pricing', label: 'Pricing', href: '/pricing' },
];

export const HERO_COPY = {
  // The headline splits so the accent phrase can stay on one line and carry
  // the hand-drawn underline that draws itself in on load.
  headlineLead: 'Scale your growth with an',
  headlineAccent: 'AI content engine',
  subheadline:
    'Create, edit, multiply content on auto-pilot. Turn any content idea into a video.',
  placeholder: 'Describe the video you want to create',
  submitLabel: 'Create my video',
  riskReversal: '$10 free credit. No card required. No commitment.',
};

export const PROMPT_SUGGESTIONS = [
  {
    id: 'app-ugc',
    label: 'App UGC',
    prompt:
      'Create a vertical UGC video that introduces my app, demonstrates its main benefit, and ends with a clear download call to action.',
  },
  {
    id: 'product-ugc',
    label: 'Product UGC',
    prompt:
      'Create a vertical UGC video for my physical product with a natural hook, a believable demonstration, and a clear purchase call to action.',
  },
  {
    id: 'app-ad',
    label: 'App ad',
    prompt:
      'Create a performance focused vertical ad for my app with three hook options, a clear product demonstration, and a direct download call to action.',
  },
  {
    id: 'professional-product-ad',
    label: 'Professional product ad',
    prompt:
      'Create a polished vertical product ad with premium art direction, clear product benefits, and a direct purchase call to action.',
  },
  {
    id: 'long-form-youtube-explainer',
    label: 'Long form YouTube explainer',
    prompt:
      'Create a structured long form YouTube explainer with a strong opening, clear chapters, visual examples, and a concise conclusion.',
  },
];

export const SHOWCASE_COPY = {
  label: 'Made with Latted',
  heading: 'AI video without the slop.',
  body: 'Latted creates realistic videos in any complexity. Made to avoid the generic AI look. These are one-shot raw agent results from a single prompt - no extra edits made.',
  disclosure: 'Brand names identify the creative subject and do not imply endorsement.',
  moreLink: 'See all examples',
};

export const SHOWCASE_ITEMS = [
  {
    id: 'kalshi',
    aspect: '9:16',
    logo: '/brand-logos/kalshi.png',
    logoAlt: 'Kalshi example video made with Latted',
    videoUrl: 'https://latted.b-cdn.net/examples/kalshi.mp4',
  },
  {
    id: 'perplexity',
    aspect: '9:16',
    logo: '/brand-logos/perplexity.png',
    logoAlt: 'Perplexity example video made with Latted',
    videoUrl: 'https://latted.b-cdn.net/examples/perplexity.mp4',
  },
  {
    id: 'huel',
    aspect: '9:16',
    logo: '/brand-logos/huel.png',
    logoAlt: 'Huel example video made with Latted',
    videoUrl: 'https://latted.b-cdn.net/examples/huel.mp4',
  },
  {
    id: 'tabs',
    aspect: '9:16',
    logo: '/brand-logos/tabs.png',
    logoAlt: 'Tabs Chocolate example video made with Latted',
    videoUrl: 'https://latted.b-cdn.net/examples/tabs.mp4',
  },
  {
    id: 'skims',
    aspect: '9:16',
    logo: '/brand-logos/skims.png',
    logoAlt: 'Skims example video made with Latted',
    videoUrl: 'https://latted.b-cdn.net/examples/skims.mp4',
  },
];

export const AGENT_COPY = {
  label: 'The Composer agent',
  heading: 'Watch it plan, shoot, and cut your video.',
  body: 'One prompt in. Composer writes the shot list, generates every shot, carries continuity between them, scores the cut, and edits the timeline.',
  supporting:
    'When it finishes, refine any shot yourself in the same editor, or ask Composer for another pass.',
  prompt: 'Make a 30 second launch ad for our new running shoe.',
  visualAlt:
    'The Composer agent works through a video: it reads brand context, writes a shot list, generates each shot, extracts the last frame to keep continuity between them, generates a transition, scores the music, edits the timeline, and renders the finished video.',
};

/**
 * Scripted replay of a real Composer run. Titles and order mirror the
 * agent tools that actually run in production (image and video
 * generation, last-frame continuity, transitions, music, timeline
 * edits, render) so the landing visual never promises a fake pipeline.
 */
export const AGENT_RUN_STEPS = [
  {
    id: 'context',
    kind: 'think',
    title: 'Reading your brand context',
    meta: 'site, guidelines, past ads',
    duration: 1400,
  },
  {
    id: 'script',
    kind: 'tool',
    title: 'Writing the shot list',
    meta: '6 shots · 30 seconds',
    duration: 1500,
  },
  {
    id: 'image-1',
    kind: 'tool',
    title: 'Generate image',
    meta: 'shot 1 keyframe',
    duration: 1400,
  },
  {
    id: 'video-1',
    kind: 'tool',
    title: 'Generate video',
    meta: 'shot 1',
    duration: 1900,
    wait: true,
    clip: 1,
  },
  {
    id: 'last-frame',
    kind: 'tool',
    title: 'Extract last frame',
    meta: 'continuity into shot 2',
    duration: 1200,
  },
  {
    id: 'video-2',
    kind: 'tool',
    title: 'Generate video',
    meta: 'shot 2',
    duration: 1700,
    wait: true,
    clip: 2,
  },
  {
    id: 'music',
    kind: 'tool',
    title: 'Generate music',
    meta: 'scored to the cut',
    duration: 1300,
    music: true,
  },
  {
    id: 'transition',
    kind: 'tool',
    title: 'Generate transition',
    meta: 'shot 2 into shot 3',
    duration: 1500,
    clip: 3,
  },
  {
    id: 'edit',
    kind: 'tool',
    title: 'Editing the timeline',
    meta: 'captions, pacing, brand color',
    duration: 1600,
    captions: true,
  },
  {
    id: 'render',
    kind: 'tool',
    title: 'Render',
    meta: '1080 × 1920 · 30s',
    duration: 2000,
    final: true,
  },
];

export const TAGLINE_LINES = [
  'One human idea should not become one video.',
  'It should become an entire content system.',
];

export const FACTORY_COPY = {
  label: 'The content factory',
  heading: 'From brief to performance data, every stage stays connected.',
  body: 'Build the workflow once. Latted creates on schedule, sends work for review, publishes approved content, and carries the results into the next cycle.',
  flowAlt:
    'An automated workflow builds itself node by node and then runs: it researches competitor Reels on Instagram and top ads on TikTok, analyzes the Meta Ads dashboard, ideates angles, generates videos, sends them for approval in Slack, and publishes them to Meta Ads.',
};

/**
 * Nodes of the workflow that assembles itself in the factory section.
 * The order is the order the connectors draw and the run travels.
 */
export const FACTORY_FLOW_NODES = [
  { id: 'instagram', title: 'Research Instagram', meta: 'competitor Reels' },
  { id: 'tiktok', title: 'Research TikTok', meta: 'top performing ads' },
  { id: 'meta-ads', title: 'Analyze Meta Ads', meta: 'CPI, ROAS, winners' },
  { id: 'ideate', title: 'Ideate angles', meta: 'hooks to test' },
  { id: 'generate', title: 'Generate videos', meta: '6 variations' },
  { id: 'approve', title: 'Send for approval', meta: 'Slack review' },
  { id: 'publish', title: 'Publish', meta: 'Meta Ads' },
];

export const FACTORY_STAGES = [
  {
    id: 'direction',
    title: 'Set the direction',
    outcome: 'Start with a commercial goal, not a blank timeline.',
    detail:
      'Describe the video, attach your website, product material, brand assets, guidelines, or past creative.',
  },
  {
    id: 'create',
    title: 'Create and edit',
    outcome: 'Move from brief to editable video in one system.',
    detail:
      'Latted pairs leading generative video models with a built in editing agent and a full editor for precise final changes.',
  },
  {
    id: 'review',
    title: 'Review on your terms',
    outcome: 'Automation does not remove approval.',
    detail:
      'Choose workflow checkpoints, receive a Slack notification, review the generated work, and make changes before anything goes live.',
  },
  {
    id: 'publish',
    title: 'Publish organic and paid creative',
    outcome: 'Run one production system across every growth channel.',
    detail:
      'Prepare and distribute creative for TikTok, Instagram, YouTube, Meta Ads, Google Ads, and TikTok Ads.',
  },
  {
    id: 'measure',
    title: 'Measure commercial performance',
    outcome: 'Connect creative output to the result it produced.',
    detail:
      'Bring views, acquisition cost, and return on ad spend back into the workflow instead of leaving performance in a separate dashboard.',
  },
  {
    id: 'improve',
    title: 'Improve the next cycle',
    outcome: 'Every run gives the next run more context.',
    detail:
      'Use direct feedback, brand history, past content, and performance data to improve what the system creates next.',
  },
];

export const FORCE_MULTIPLIER_COPY = {
  heading: 'Multiply One Video Into A Thousand Possibilities',
  scale: 'One video can become 10 languages, 100 characters, and 1,000 variations.',
  // The source frame reuses an example from the showcase above rather
  // than a mock, so the branching starts from something real.
  sourceExampleId: 'perplexity',
  branches: [
    {
      id: 'languages',
      label: 'Languages',
      count: '10',
      // Flag plus code: the code carries the meaning on platforms that
      // do not render regional-indicator emoji (notably Windows Chrome).
      items: [
        { flag: '\u{1F1FA}\u{1F1F8}', text: 'EN' },
        { flag: '\u{1F1EA}\u{1F1F8}', text: 'ES' },
        { flag: '\u{1F1E9}\u{1F1EA}', text: 'DE' },
        { flag: '\u{1F1EF}\u{1F1F5}', text: 'JA' },
      ],
      more: '+6',
    },
    {
      id: 'characters',
      label: 'Characters',
      count: '100',
      items: [{ text: 'Maya' }, { text: 'Diego' }, { text: 'Aiko' }, { text: 'Noah' }],
      more: '+96',
    },
    {
      id: 'variations',
      label: 'Variations',
      count: '1,000',
      items: [{ text: 'Hook A' }, { text: 'Hook B' }, { text: 'Delivery A' }, { text: 'CTA B' }],
      more: '+996',
    },
  ],
};

export const BRAND_CONTROL_COPY = {
  label: 'Built around your brand',
  heading: 'The system learns the context separate tools lose.',
  body: 'Give Latted your website, assets, guidelines, past content, performance data, and direct feedback. The same context can carry from the original brief through editing, approval, distribution, and the next iteration.',
  inputs: [
    'Website and product context',
    'Brand assets and guidelines',
    'Past content',
    'Performance data',
    'Direct feedback',
  ],
  sequence: ['Scheduled workflow', 'Slack notification', 'Review', 'Edit if needed', 'Publish'],
  slackMessage: 'Three videos are ready for review.',
  slackActions: ['Review output', 'Open workflow'],
};

export const PLATFORM_COVERAGE_COPY = {
  heading: 'One production system for organic and paid growth.',
  body: 'Create for the channel, preserve the brand, and keep the performance loop connected.',
  groups: [
    {
      id: 'organic',
      label: 'Organic',
      platforms: [
        { id: 'tiktok', name: 'TikTok', planned: false },
        { id: 'instagram', name: 'Instagram', planned: false },
        { id: 'youtube', name: 'YouTube', planned: false },
      ],
    },
    {
      id: 'paid',
      label: 'Paid',
      platforms: [
        { id: 'meta-ads', name: 'Meta Ads', planned: false },
        { id: 'google-ads', name: 'Google Ads', planned: true },
        { id: 'tiktok-ads', name: 'TikTok Ads', planned: false },
      ],
    },
  ],
};

export const RESULTS_COPY = {
  label: 'Results from the loop',
  heading: 'More creative only matters when it improves growth.',
  stats: [
    { id: 'videos-shared', value: 30000, display: '30,000', countUp: true, label: 'videos shared' },
    {
      id: 'lower-cpi',
      value: 40,
      display: '40% lower',
      countUp: true,
      suffix: '%',
      trailing: ' lower',
      label: 'CPI than the industry average across Latted paid ads',
    },
    {
      id: 'roas-two-weeks',
      value: null,
      display: 'Within two weeks',
      countUp: false,
      label: 'the average user sees higher ROAS',
    },
  ],
};

export const FAQ_ITEMS = [
  {
    id: 'just-ai-video-generator',
    question: 'Is Latted just an AI video generator?',
    answer:
      'No. Composer can create a single video from a prompt, but Latted is designed as a connected content engine. You can edit the output, turn the process into a reusable workflow, schedule creation, review results, publish, and use performance feedback to improve the next run.',
  },
  {
    id: 'brand-consistency',
    question: 'How does Latted keep content consistent with my brand?',
    answer:
      'Latted can use your website, product context, brand assets, guidelines, past content, performance data, and direct feedback. Keeping that context in one system reduces the information loss that happens when separate tools handle each stage.',
  },
  {
    id: 'edit-output',
    question: 'Can I edit what Latted creates?',
    answer:
      'Yes. Open any generated video in the built in editor for precise changes, or ask Composer to make larger revisions. You decide which workflow stages require review before publishing.',
  },
  {
    id: 'automatic-publishing',
    question: 'Can workflows publish automatically?',
    answer:
      'Yes, when the selected workflow and connected platform support publishing. You choose the schedule and approval checkpoints. A common setup is to receive a Slack notification, review the output, edit if needed, and then publish.',
  },
  {
    id: 'supported-channels',
    question: 'Which channels does Latted support?',
    answer:
      'The landing page presents TikTok, Instagram, YouTube, Meta Ads, Google Ads, and TikTok Ads. Before launch, verify the live status of each integration. Any unavailable integration must be labeled Planned or removed until it is available.',
  },
  {
    id: 'organic-and-paid',
    question: 'Can Latted create both organic content and paid ads?',
    answer:
      'Yes. The same system can create short or long form organic video and paid creative. Workflows can be designed around different formats, audiences, hooks, characters, languages, and calls to action.',
  },
  {
    id: 'after-first-prompt',
    question: 'What happens after I submit my first prompt?',
    answer:
      'You create an account, receive $10 in free credit, and Latted begins the saved prompt automatically. No card is required. When the video is ready, you can review and edit it inside Latted.',
  },
  {
    id: 'contract-lock-in',
    question: 'Am I locked into a contract?',
    answer:
      'No. There is no commitment. After using the free credit, choose a paid option on the pricing page if you want to continue.',
  },
  {
    id: 'content-ownership',
    question: 'Who owns the content?',
    answer:
      'You retain ownership of the prompts and assets you upload and, to the extent permitted by applicable law, the video content you generate. Usage remains subject to the Latted Terms of Service and the rights associated with any third party material you provide.',
  },
  {
    id: 'replace-creative-team',
    question: 'Does Latted replace my creative team?',
    answer:
      'Latted is a force multiplier for human judgment. People still define the product story, brand standard, commercial goal, and approval rules. Latted scales the repetitive production, adaptation, distribution, and feedback work around those decisions.',
  },
];

export const FINAL_CTA_COPY = {
  heading: 'Put your first video into production.',
  body: 'Start with one prompt. Keep the $10 free credit. Build the workflow after you see the output.',
  placeholder: 'What do you want to create?',
  riskReversal: 'No card required. No commitment.',
};

export const FOOTER_LINK_GROUPS = [
  {
    id: 'product',
    title: 'Product',
    links: [
      { label: 'Examples', href: '/examples' },
      { label: 'Workflows', href: '/workflows' },
      { label: 'Integrations', href: '/integrations' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    links: [
      { label: 'Blog', href: '/blog' },
      { label: 'Terms', href: '/terms-of-service' },
      { label: 'Privacy', href: '/privacy-policy' },
    ],
  },
  {
    id: 'account',
    title: 'Account',
    links: [
      { label: 'Sign in', action: 'sign-in' },
      { label: 'Create a video', action: 'create-video' },
    ],
  },
];
